import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import heroLight from '@/assets/hero-light.webp'
import heroLightMd from '@/assets/hero-light-md.webp'
import heroLightSm from '@/assets/hero-light-sm.webp'
import heroDark from '@/assets/hero-dark.webp'
import heroDarkMd from '@/assets/hero-dark-md.webp'
import heroDarkSm from '@/assets/hero-dark-sm.webp'
/*
  Иллюстрации карточек — отдельные файлы, не те же, что в карусели входа.
  В макете у этих картинок задана обрезка (imageTransform): прозрачные
  поля вокруг предмета срезаны, и предмет занимает всю плашку 324x188.
  Тот же файл без обрезки даёт предмет вдвое мельче — что и было видно
  при сверке с макетом.
*/
import art1 from '@/assets/feature-1.webp'
import art2 from '@/assets/feature-2.webp'
import art3 from '@/assets/feature-3.webp'
import art4 from '@/assets/feature-4.webp'
import { SUPPORT_URL } from '@/shared/lib/contacts'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import type { Currency } from '@/shared/api/types'
import { CoinIcon } from '@/shared/ui/CoinIcon'
import { Logo } from '@/shared/ui/Logo'
import { MoonIcon, SunIcon } from '@/shared/ui/icons'
import { FacebookIcon, GithubIcon, TgCircleIcon, VkIcon } from '@/shared/ui/social'
import { useAuth, useUi } from '@/stores/RootStore'
import styles from './Home.module.css'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/**
 * Преимущества. Порядок и тексты — из макета, дословно.
 *
 * Картинка чередует сторону: первая карточка — текст слева, вторая —
 * справа, и так далее. На широком экране это делает CSS через
 * :nth-child(even), в разметке порядок всегда «сначала текст»: читалке
 * и клавиатуре важен смысловой порядок, а не визуальный.
 */
/*
  Перенос в заголовке проставлен вручную — так в макете на всех трёх
  ширинах, и не «потому что не влезло»: заголовки ломаются по смыслу,
  а высота всех четырёх карточек от этого одинаковая. Отдать перенос
  браузеру значит получить на телефоне карточки разной высоты.
*/
const FEATURES = [
  { art: art1, title: 'Приём платежей\nв криптовалюте', note: 'Поддержка популярных криптовалют для оплаты.' },
  { art: art2, title: 'Комиссия\nне более 5%', note: 'Выгодные условия для бизнеса и партнёров.' },
  { art: art3, title: 'Управление\nактивами', note: 'Балансы, поступления, выводы и история операций.' },
  { art: art4, title: 'Расширение\nвозможностей', note: 'Инструменты для работы с платежами и оборотами.' },
] as const

const COINS: Currency[] = ['btc', 'ltc', 'gram', 'xmr', 'usdt_trc20', 'usdt_ton']

/*
  Фоны хиро под ширину экрана. Тема выбирается в JS, а НЕ через
  `prefers-color-scheme` в <picture>: в приложении есть свой
  переключатель темы, он ставит `data-theme` на корне, и медиазапрос
  о нём ничего не знает. Со связкой через медиазапрос человек нажимает
  «тёмная тема», страница чернеет, а фон остаётся светлым.
*/
const HERO = {
  light: `${heroLightSm} 750w, ${heroLightMd} 1024w, ${heroLight} 1920w`,
  dark: `${heroDarkSm} 750w, ${heroDarkMd} 1024w, ${heroDark} 1920w`,
}

/**
 * Витрина сервиса.
 *
 * Единственная страница, куда попадают без входа и без ссылки
 * из магазина, поэтому она отвечает на два вопроса сразу: что это
 * и как связаться. Никаких запросов к API она не делает — падать
 * ей нечем, даже когда бекенд лежит.
 */
export const HomePage = observer(function HomePage() {
  useMeta('', true)
  const auth = useAuth()
  const ui = useUi()
  const dark = ui.theme === 'dark'
  // Вошедшему «Войти» предлагать незачем — он уже вошёл.
  const entry = auth.authenticated
    ? { to: '/app', label: 'В кабинет' }
    : { to: '/login', label: 'Войти' }

  const write = (
    <a className={styles.btnPrimary} href={SUPPORT_URL}
       target="_blank" rel="noopener noreferrer">Написать нам</a>
  )

  return (
    <div className={styles.page}>
      <header className={styles.nav}>
        <Link className={styles.logo} to="/" aria-label="CryptoChangeK">
          <Logo className={styles.logoWide} />
        </Link>

        <div className={styles.navActions}>
          <button type="button" className={styles.btnIcon}
                  onClick={() => ui.toggleTheme()}
                  aria-label={dark ? 'Светлая тема' : 'Тёмная тема'}>
            {dark ? <SunIcon /> : <MoonIcon />}
          </button>
          {/* На телефоне кнопки нет: «Написать нам» — первая кнопка экрана,
              а место в шапке нужно полной вывеске. */}
          <a className={styles.btnGhost} href={SUPPORT_URL}
             target="_blank" rel="noopener noreferrer">Написать нам</a>
          <Link className={styles.btnPrimary} to={entry.to}>{entry.label}</Link>
        </div>
      </header>

      <section className={styles.hero}>
        <img className={styles.heroArt} alt="" aria-hidden="true" decoding="async"
             src={dark ? heroDark : heroLight}
             srcSet={dark ? HERO.dark : HERO.light}
             sizes="100vw" />

        <div className={styles.heroInner}>
          <div className={styles.headGroup}>
            <h1 className={styles.h1}>Принимайте криптоплатежи для бизнеса</h1>
            <p className={styles.lead}>
              Крипто-шлюз для приёма платежей, управления активами и вывода средств
              с комиссией до 5%.
            </p>
          </div>
          <div className={styles.heroActions}>
            {write}
            <Link className={styles.btnGhost} to={entry.to}>{entry.label}</Link>
          </div>
        </div>
      </section>

      <div className={styles.sheet}>
        <section className={styles.block}>
          <div className={styles.blockHead}>
            <h2 className={styles.h2}>Преимущества</h2>
            <p className={styles.sub}>
              Основные возможности для приёма криптоплатежей, контроля средств
              и работы с оборотами.
            </p>
          </div>

          <ul className={styles.features}>
            {FEATURES.map((item) => (
              <li key={item.title} className={styles.feature}>
                <div className={styles.featureText}>
                  <h3 className={styles.h3}>{item.title}</h3>
                  <p className={styles.note}>{item.note}</p>
                </div>
                <img className={styles.featureArt} src={item.art} alt=""
                     loading="lazy" decoding="async" />
              </li>
            ))}
          </ul>

          {write}
        </section>

        <section className={styles.block}>
          <div className={styles.blockHead}>
            <h2 className={styles.h2}>Доступные валюты</h2>
            <p className={styles.sub}>
              Принимайте платежи в популярных криптовалютах и отслеживайте балансы
              в личном кабинете
            </p>
          </div>

          <ul className={styles.coins}>
            {COINS.map((currency) => {
              const coin = coinOf(currency)
              // Сеть подписываем только у USDT: он живёт в двух сетях, и
              // адреса несовместимы. У остальных сеть одна — подпись была бы шумом.
              const showNet = coin.networks.length > 1
              return (
                <li key={currency} className={styles.coin}>
                  <span className={styles.coinMark} aria-hidden="true">
                    <CoinIcon currency={currency} size={32} />
                  </span>
                  <span className={styles.coinNames}>
                    <span className={styles.coinTicker}>{coin.ticker}</span>
                    {showNet && <span className={styles.coinNet}>{networkOf(currency)}</span>}
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      </div>

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <div className={styles.headGroup}>
            <h2 className={styles.h1}>Планируете принимать платежи через крипту?</h2>
            <p className={styles.lead}>
              Свяжитесь с нами, чтобы обсудить возможность подключения и условия
              сотрудничества.
            </p>
          </div>
          {write}
        </div>

        <footer className={styles.foot}>
          <span className={styles.footText}>{`© CryptoChangeK, ${new Date().getFullYear()}`}</span>
          <div className={styles.socials}>
            <a className={styles.social} href={SUPPORT_URL}
               target="_blank" rel="noopener noreferrer" aria-label="Telegram">
              <TgCircleIcon />
            </a>
            {/* Остальные площадки показываем только тогда, когда адрес задан:
                мёртвая иконка в подвале хуже отсутствующей. */}
            {import.meta.env.VITE_VK_URL && (
              <a className={styles.social} href={import.meta.env.VITE_VK_URL}
                 target="_blank" rel="noopener noreferrer" aria-label="ВКонтакте">
                <VkIcon />
              </a>
            )}
            {import.meta.env.VITE_FACEBOOK_URL && (
              <a className={styles.social} href={import.meta.env.VITE_FACEBOOK_URL}
                 target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FacebookIcon />
              </a>
            )}
            {import.meta.env.VITE_GITHUB_URL && (
              <a className={styles.social} href={import.meta.env.VITE_GITHUB_URL}
                 target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <GithubIcon />
              </a>
            )}
          </div>
        </footer>
      </section>
    </div>
  )
})
